import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  incomingPhotoPath,
  incomingThumbPathOf,
  type DistrictPhotoConfig,
} from '@citizen-walk/shared-types';
import { storage } from '../../lib/firebase';
import { ensureDistrictAccessClaim } from './ensureDistrictClaim';
import { assertPhotoSize, generateThumbnail, preprocessPhoto } from './photo';

/**
 * 写真 upload 共通 helper (R-27 docs/98 解消、Phase 5)。
 *
 * 旧 createPost / updatePost で重複していた:
 *   1. preprocessPhoto + generateThumbnail 並列生成
 *   2. uploadBytes × 2 (original.jpg + thumbnail.jpg)
 * の logic を 1 関数に集約。
 *
 * Storage path 規約 (docs/04 §4-2):
 *   - original: `districts/{districtId}/posts/{postId}/original.jpg`
 *   - thumbnail: `districts/{districtId}/posts/{postId}/thumbnail.jpg`
 *
 * Phase 4 onPhotoUpload trigger (cb40b63) が thumbnail を品質保証で再生成 (冪等)。
 * 上書き upload も対応 (storage.rules R-19 で create + update 許可)。
 */

export const POST_PHOTO_PATHS = {
  original: (districtId: string, postId: string) =>
    `districts/${districtId}/posts/${postId}/original.jpg`,
  thumbnail: (districtId: string, postId: string) =>
    `districts/${districtId}/posts/${postId}/thumbnail.jpg`,
};

/**
 * upload helper の戻り値 (docs/106 オフライン再表示強化)。
 *
 * `photoUrl` / `photoThumbUrl` は upload 直後の `getDownloadURL` 結果で、投稿 doc に保存して
 * Firestore 永続キャッシュ + SW CacheFirst でオフライン再表示を可能にするための old2 方式。
 *   - photoUrl (original.jpg): onPhotoUpload trigger が触らないため token 恒久安定。
 *   - photoThumbUrl (thumbnail.jpg): trigger が再生成で token 上書きするため best-effort
 *     (表示層 usePhotoUrl が online 時の getDownloadURL で自己修復する)。
 */
export interface UploadPhotoResult {
  photoPath: string;
  photoThumbPath: string;
  photoUrl: string;
  photoThumbUrl: string;
}

/**
 * Blob を直接 upload (preprocess 済の場合、outbox flush 経路で使用)。
 */
export async function uploadProcessedPhotoBlobs(
  districtId: string,
  postId: string,
  photoBlob: Blob,
  photoThumbBlob: Blob,
  mimeType = 'image/jpeg',
): Promise<UploadPhotoResult> {
  // storage-4 (docs/111 Wave 1): upload 前に地区 claim を最新化し、古い token で Storage 規則
  // (canAccessDistrict) に弾かれて写真投稿が丸ごと失敗するのを防ぐ。best-effort (失敗しても続行)。
  await ensureDistrictAccessClaim(districtId);
  const photoPath = POST_PHOTO_PATHS.original(districtId, postId);
  const photoThumbPath = POST_PHOTO_PATHS.thumbnail(districtId, postId);
  const originalRef = storageRef(storage, photoPath);
  const thumbRef = storageRef(storage, photoThumbPath);
  await Promise.all([
    uploadBytes(originalRef, photoBlob, { contentType: mimeType }),
    uploadBytes(thumbRef, photoThumbBlob, { contentType: 'image/jpeg' }),
  ]);
  // docs/106: upload 直後に download URL を取得して doc に保存する (old2 方式)。
  const [photoUrl, photoThumbUrl] = await Promise.all([
    getDownloadURL(originalRef),
    getDownloadURL(thumbRef),
  ]);
  return { photoPath, photoThumbPath, photoUrl, photoThumbUrl };
  // Phase 4 onPhotoUpload trigger が thumbnail を品質保証で再生成 (冪等) し、
  // photoThumbUrl の token を canonical 値に best-effort update する (docs/106 §4)。
}

/**
 * docs/28 §4-3: 写真を本人専用の一時置き場 (`districts/{d}/incoming/{uid}/{postId}.jpg`) に上げる。
 * サーバーが顔・ナンバーをぼかしてから `posts/{postId}/` に公開し、投稿 doc に URL を書く。
 * 他の参加者は一時置き場を読めないので、ぼかす前の写真がほかの人の画面や端末のキャッシュに
 * 載らない。公開用のサムネはサーバーが作る。`thumbBlob` を渡すと、処理が終わるまで本人の一覧・
 * 地図に出す小さいサムネを `{postId}_thumb.jpg` に一緒に上げる (原寸を一覧に出さない、レビュー A4)。
 *
 * 新規投稿 (doc 作成前) と写真の編集 (既存 doc) の両方で使う。編集では doc の写真の項目に触らず、
 * サーバー (onPhotoUpload) がぼかし直して公開する。
 */
export async function uploadIncomingPhoto(
  districtId: string,
  uid: string,
  postId: string,
  photoBlob: Blob,
  mimeType = 'image/jpeg',
  thumbBlob?: Blob | null,
): Promise<string> {
  await ensureDistrictAccessClaim(districtId);
  const path = incomingPhotoPath(districtId, uid, postId);
  // サムネを先に上げる (原寸の finalize でサーバーの処理が始まり、公開後にサムネも消すため)。
  if (thumbBlob) {
    await uploadBytes(storageRef(storage, incomingThumbPathOf(path)), thumbBlob, {
      contentType: 'image/jpeg',
    });
  }
  await uploadBytes(storageRef(storage, path), photoBlob, { contentType: mimeType });
  return path;
}

/** 本人の一時置き場の写真とサムネ (写真を外す編集で、処理前の写真が公開されないよう消す)。 */
export function ownIncomingPaths(districtId: string, uid: string, postId: string): string[] {
  const path = incomingPhotoPath(districtId, uid, postId);
  return [path, incomingThumbPathOf(path)];
}

/**
 * File から preprocess + thumbnail 生成して upload (createPost / updatePost 経路で使用)。
 *
 * Task C: 地区別 `photoConfig` で圧縮パラメータ / 無圧縮 / 受付上限を制御する。
 *   - `uploadOriginal=true` なら元ファイルを無圧縮で保存 (original の MIME を維持)。
 *   - 最終 blob が受付上限 (maxUploadSizeMB、既定 10MB) を超える場合は throw (呼出側が toast)。
 *   - サムネは常に固定パラメータで JPEG 生成 (一覧 performance のため)。
 */
export async function uploadNewPhoto(
  districtId: string,
  postId: string,
  file: File,
  config?: DistrictPhotoConfig,
): Promise<UploadPhotoResult> {
  const [processed, thumb] = await Promise.all([
    preprocessPhoto(file, config),
    generateThumbnail(file),
  ]);
  assertPhotoSize(processed, config);
  const mimeType = config?.uploadOriginal ? file.type || 'image/jpeg' : 'image/jpeg';
  return await uploadProcessedPhotoBlobs(districtId, postId, processed, thumb, mimeType);
}

/**
 * 既存 photo を Storage から best-effort 削除 (deletePost / updatePost 削除経路で使用)。
 * 失敗は console.warn のみ (dangling reference 許容、postId 推測困難で実害低)。
 */
export async function deletePhotoFromStorage(
  photoPath?: string | null,
  photoThumbPath?: string | null,
): Promise<void> {
  const paths = [photoPath, photoThumbPath].filter((p): p is string => !!p);
  await Promise.all(
    paths.map(async (p) => {
      try {
        await deleteObject(storageRef(storage, p));
      } catch (e) {
        console.warn(`deletePhotoFromStorage: failed for ${p}`, e);
      }
    }),
  );
}
